import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { MapPin, Phone, Mail, Calendar, Globe, MapPinned } from "lucide-react";
import { blackUser } from "@/assets";
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { Separator } from "@/components/ui/separator";
import AccountActivity from "@/components/backoffice/all-staff/staff-profile/account-activity";
import Sceurity from "@/components/backoffice/all-staff/staff-profile/security";
import Notification from "@/components/backoffice/all-staff/staff-profile/nofification";
import Leaves from "@/components/backoffice/all-staff/staff-profile/leaves";
import { getSingleStaff, updateStaff } from "@/api/new-api/staff-management/staff/staff";
import { getStaffLeavesById } from "@/api/new-api/staff-management/leave/leave";
import { AddLeaveDialog } from "@/components/dialog/staff-manage/add-leave-dialog";
import Loading from "@/components/common/loading";
import FileUploader from "@/components/common/file-uploader";
import useAuth from "@/hooks/useAuth";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";

import moment from "moment";

function UserProfile() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("tabs-data")) || {};
      return stored["staff-manage-staff-user"]?.["default"] || "account";
    } catch {
      return "account";
    }
  });
  const [singleStaff, setSingleStaff] = useState(null);
  const [staffLeaves, setStaffLeaves] = useState([]);
  const [openAddLeave, setOpenAddLeave] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leavesLoading, setLeavesLoading] = useState(false);
  const [files, setFiles] = useState([]);

  const currentStaffId = user?.staff_id || user?.staff?.id || user?.id;

  const fetchSingleStaff = async () => {
    if (!currentStaffId) return;
    setLoading(true);
    try {
      const staffRes = await getSingleStaff(currentStaffId);
      if (staffRes?.status === "success") {
        setSingleStaff(staffRes.data?.staff || null);
      }
    } catch (error) {
      console.error("Failed to fetch staff data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaves = async () => {
    if (!currentStaffId) return;
    setLeavesLoading(true);
    try {
      const leaveRes = await getStaffLeavesById(currentStaffId);
      if (leaveRes?.status === "success") {
        setStaffLeaves(leaveRes.data?.leaves || []);
      }
    } catch (error) {
      console.error("Failed to fetch leaves:", error);
    } finally {
      setLeavesLoading(false);
    }
  };

  useEffect(() => {
    if (currentStaffId) {
      fetchSingleStaff();
    }
  }, [currentStaffId]);

  useEffect(() => {
    if (currentStaffId && activeTab === "leaves") {
      fetchLeaves();
    }
  }, [currentStaffId, activeTab]);

  const handleUploadComplete = async (uploadedFiles) => {
    const fileId = uploadedFiles?.[0]?.file_id;
    const fileUrl = uploadedFiles?.[0]?.file_url || uploadedFiles?.[0]?.url;
    if (!fileId) return;

    const staffId = singleStaff?.id || currentStaffId;

    try {
      const staff = singleStaff || {};
      const staffUser = staff.user || user || {};
      const rawShifts = Array.isArray(staff.shift_schedule)
        ? staff.shift_schedule.map((s) => s?.id || s)
        : [1];

      const rawRoleId = staff.role_id ?? staffUser.role_id ?? null;

      const payload = {
        first_name: staffUser.first_name || staff.first_name || user?.first_name || "Staff",
        last_name: staffUser.last_name || staff.last_name || user?.last_name || "User",
        email: staffUser.email || staff.email || user?.email || "",
        phone: staffUser.phone || staff.phone || user?.phone || "",
        role_id: rawRoleId ? Number(rawRoleId) : null,
        joining_date: staff.joining_date ? moment(staff.joining_date).format("YYYY-MM-DD") : "2026-01-01",
        gender: staff.gender || "male",
        address: staff.address || "N/A",
        designation: staff.designation || user?.designation || "Staff",
        employment_type: staff.employment_type || "permanent",
        salary_type: staff.salary_type || "monthly",
        salary: staff.salary != null ? String(staff.salary) : "0",
        shift_schedule_id: "1",
        shifts: rawShifts.length > 0 ? rawShifts : [1],
        currency: staff.currency || "GBP",
        bank_name: staff.bank_name || "",
        bank_account_number: staff.bank_account_number || "",
        bank_branch_name: staff.bank_branch_name || "",
        restaurant_id: Number(user?.restaurant_id || staff.branch_id || 1),
        file_manager_id: fileId,
      };

      const res = await updateStaff(staffId, payload);
      if (res?.status === "success" || res?.message?.toLowerCase()?.includes("success")) {
        toast.success("Profile photo updated successfully!");
      } else {
        toast.error(res?.message || "Failed to update profile photo.");
      }

      setSingleStaff((prev) => ({
        ...prev,
        photo: fileUrl || prev?.photo,
        file_manager_url: fileUrl || prev?.file_manager_url,
        file_manager_id: fileId,
        user: {
          ...prev?.user,
          photo: fileUrl || prev?.user?.photo,
          file_manager_url: fileUrl || prev?.user?.file_manager_url,
        },
      }));

      try {
        const storedUser = JSON.parse(localStorage.getItem("user") || "null");
        if (storedUser) {
          localStorage.setItem(
            "user",
            JSON.stringify({
              ...storedUser,
              photo: fileUrl || storedUser.photo,
              file_manager_url: fileUrl || storedUser.file_manager_url,
            })
          );
        }
      } catch (e) {
        console.error("Local storage sync error", e);
      }
    } catch (err) {
      console.error("Failed to update staff image", err);
    }
  };

  if (loading) {
    return <Loading links={backofficeLinks} />;
  }

  const profileData = singleStaff || {};
  const userData = singleStaff?.user || user || {};

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
      pageName={{ icon: true, title: `My Account` }}
    >
      <div className="container grid grid-cols-1 md:grid-cols-3 gap-4 py-5">
        {/* Left Side Profile */}
        <Card className="col-span-1 border border-neutral-700">
          <CardHeader>
            <div className="flex justify-between">
              <div className="flex items-center w-full justify-between gap-4">
                <div className="flex gap-4 flex-col xl:flex-row lg:items-center">
                  <Avatar className="w-24 h-24 rounded-md border border-neutral-700 overflow-hidden">
                    <AvatarImage
                      className="h-full w-full object-cover"
                      src={
                        profileData?.photo ||
                        profileData?.file_manager_url ||
                        userData?.photo ||
                        userData?.file_manager_url ||
                        blackUser
                      }
                      alt={userData?.first_name || userData?.name || "User"}
                    />
                  </Avatar>
                  <div>
                    <h2 className="text-xl text-cwhite font-bold">
                      {userData?.first_name ? `${userData.first_name} ${userData.last_name || ""}` : (userData?.name || "Staff Name")}
                    </h2>
                    <p className="text-sm text-cwhite3">
                      {profileData?.designation || userData?.designation || "N/A"}
                    </p>
                    <div className="mt-2">
                      <FileUploader
                        files={files}
                        multiple={false}
                        replace={true}
                        restaurantId={user?.restaurant_id}
                        directory={`restaurant/${user?.restaurant_id || 1}/staff/image`}
                        onFilesChange={(updatedFiles) => setFiles(updatedFiles)}
                        onUploadComplete={handleUploadComplete}
                        label="Upload Image"
                        type="button"
                      />
                    </div>
                  </div>
                </div>
                <div className="h-full flex flex-col xl:justify-between gap-8 pb-3 items-end">
                  <span className="text-cgreen flex items-center gap-1">
                    <span className="text-[8px]">🟢 </span>Active
                  </span>
                  {profileData?.id && (
                    <Link
                      to={`/backoffice/staff-management/staff/update/${profileData?.id}`}
                      className="text-white hover:text-cyellow transition-colors cursor-pointer text-sm"
                    >
                      Edit
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <h2 className="text-white font-medium">Basic Info</h2>
            <Separator />
            <div className="flex flex-col gap-4 py-3 text-cwhite3">
              <div className="flex justify-between items-center">
                <strong className="flex gap-1 items-center">
                  <MapPin size={17} /> Location
                </strong>{" "}
                {profileData?.address || "Not specified"}
              </div>
              <div className="flex justify-between items-center">
                <strong className="flex gap-1 items-center">
                  <Phone size={17} />
                  Phone
                </strong>{" "}
                {profileData?.phone || userData?.phone || "Not specified"}
              </div>
              <div className="flex justify-between items-center">
                <strong className="flex gap-1 items-center">
                  <Mail size={17} />
                  Email
                </strong>{" "}
                {userData?.email || "Not specified"}
              </div>
              <div className="flex justify-between items-center">
                <strong className="flex gap-1 items-center">
                  <Calendar size={17} />
                  Join Date
                </strong>{" "}
                {profileData?.joining_date || "Not specified"}
              </div>
              <div className="flex justify-between items-center">
                <strong>Gender</strong> {profileData?.gender || "Not specified"}
              </div>
              <div className="flex justify-between items-center">
                <strong>Branch</strong>{" "}
                {profileData?.branch_name || "Not specified"}
              </div>
              <div className="flex justify-between items-center">
                <strong>Staff Type</strong>{" "}
                <span className="text-cyellow capitalize">
                  {profileData?.employment_type || "Not specified"}
                </span>
              </div>
            </div>

            <h2 className="text-white font-medium pt-2">Bank Information</h2>
            <Separator />
            <div className="flex flex-col gap-4 py-3 text-cwhite3">
              <div className="flex justify-between items-center capitalize">
                <strong>Salary Type</strong>{" "}
                {profileData?.salary_type || "Not specified"}
              </div>
              <div className="flex justify-between items-center">
                <strong>Account No</strong>{" "}
                {profileData?.bank_account_number || "Not specified"}
              </div>
              <div className="flex justify-between items-center">
                <strong>Bank</strong>{" "}
                {profileData?.bank_name || "Not specified"}
              </div>
              <div className="flex justify-between items-center">
                <strong>Currency</strong>{" "}
                {profileData?.currency || "Not specified"}
              </div>
            </div>
            <h2 className="text-white font-medium pt-2">Others Info</h2>
            <Separator />
            <div className="flex flex-col gap-4 text-cwhite3 pt-2">
              <div className="flex justify-between items-center capitalize">
                <strong className="flex gap-1 items-center">
                  <Globe size={17} />
                  Language
                </strong>{" "}
                {profileData?.language || "Not specified"}
              </div>
              <div className="flex justify-between items-center capitalize">
                <strong className="flex gap-1 items-center">
                  <MapPinned size={17} />
                  Country
                </strong>{" "}
                {profileData?.country || "Not specified"}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-transparent col-span-1 md:col-span-2">
          <Tabs
            valueKey="staff-manage-staff-user"
            className="bg-transparent relative"
            defaultValue={activeTab}
            onValueChange={(val) => setActiveTab(val)}
          >
            <TabsList className="flex justify-start w-full">
              <Separator className="absolute top-8" />
              <TabsTrigger className="bg-transparent" value="account">
                Account Activity
              </TabsTrigger>
              <TabsTrigger value="security">Security</TabsTrigger>
              <TabsTrigger className="" value="notification">
                <span className="pl-5">Notification Settings</span>
              </TabsTrigger>
              <TabsTrigger value="leaves">Leaves</TabsTrigger>
            </TabsList>
            <AccountActivity />
            <Sceurity />
            <Notification />
            <Leaves
              data={staffLeaves}
              loading={leavesLoading}
              showAddButton={true}
              onAddLeave={() => setOpenAddLeave(true)}
            />
          </Tabs>
        </Card>
      </div>

      <AddLeaveDialog
        isOpen={openAddLeave}
        onClose={() => setOpenAddLeave(false)}
        onSuccess={(newItem) => {
          if (newItem) {
            setStaffLeaves((prev) => [newItem, ...(Array.isArray(prev) ? prev : [])]);
          }
        }}
        staffId={user?.id}
      />
    </Layout2>
  );
}

export default UserProfile;
